import { subDays } from "date-fns";

import { CHALLENGE_LOCK_DAYS } from "@/lib/constants";
import { Tournament } from "@/types";

/**
 * Fields needed to evaluate when a tournament accepts new teams / pool players.
 */
type JoinPhaseInput = Pick<
  Tournament,
  | "startsAt"
  | "endsAt"
  | "signInStartsAt"
  | "signInEndsAt"
  | "allowJoinDuringTournament"
  | "archived"
>;

/**
 * Sign-in window is open: now is within [signInStartsAt, signInEndsAt].
 * An unset bound means that side is unbounded.
 */
export function isSignInOpen(
  tournament: JoinPhaseInput,
  now: Date = new Date(),
): boolean {
  const start = tournament.signInStartsAt
    ? new Date(tournament.signInStartsAt)
    : null;
  const end = tournament.signInEndsAt
    ? new Date(tournament.signInEndsAt)
    : null;
  return (!start || now >= start) && (!end || now <= end);
}

/**
 * Active-phase joining is open: the setting is on (undefined treated as on for
 * legacy tournaments), the tournament has STARTED, and it has not yet entered the
 * challenge-lock period (the final CHALLENGE_LOCK_DAYS before it ends, which
 * mirrors the challenge deadline enforced in actions/match.ts).
 *
 * This is the window where joining teams are AUTO-PLACED at the bottom of the
 * tower. Before the tournament starts (sign-in window or the pre-kickoff gap),
 * joiners instead go to the pool for the organizer to seed — see
 * isPreStartJoinOpen.
 */
export function isActiveJoinOpen(
  tournament: JoinPhaseInput,
  now: Date = new Date(),
): boolean {
  if (tournament.allowJoinDuringTournament === false) return false;
  const startsAt = new Date(tournament.startsAt);
  const endsAt = new Date(tournament.endsAt);
  const lockStartsAt = subDays(endsAt, CHALLENGE_LOCK_DAYS);
  return now >= startsAt && now < lockStartsAt;
}

/**
 * The pre-kickoff gap is open: sign-in has closed but the tournament has not yet
 * started. Joining is still allowed here (when the organizer permits joining
 * beyond sign-in), but the teams land in the POOL for the organizer to seed into
 * the tower — they are NOT auto-placed at the bottom. Requires a configured
 * sign-in end; without one there is no gap and joining stays in the sign-in
 * window until the tournament starts.
 */
export function isPreStartJoinOpen(
  tournament: JoinPhaseInput,
  now: Date = new Date(),
): boolean {
  if (tournament.allowJoinDuringTournament === false) return false;
  if (!tournament.signInEndsAt) return false;
  const signInEndsAt = new Date(tournament.signInEndsAt);
  const startsAt = new Date(tournament.startsAt);
  return now > signInEndsAt && now < startsAt;
}

/**
 * Whether a team / pool player may join right now — the sign-in window, the gap
 * before kickoff (joiners go to the pool), or the active phase (joiners are
 * auto-placed at the bottom of the tower).
 */
export function isJoinOpen(
  tournament: JoinPhaseInput,
  now: Date = new Date(),
): boolean {
  // Archived tournaments are read-only: no new teams or pool players, even if
  // the date window (sign-in / active phase) would otherwise still be open.
  // Archiving is a manual, explicit close that overrides the time-based phases.
  if (tournament.archived) return false;
  return (
    isSignInOpen(tournament, now) ||
    isPreStartJoinOpen(tournament, now) ||
    isActiveJoinOpen(tournament, now)
  );
}
